import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";
import { getStoredUser } from "../../../api/auth";
import { createBusinessBorrower, createIndividualBorrower } from "../../../api/borrowers";
import { fetchBranches } from "../../../api/users";
import { runCrud } from "../Common/credoraToast";

const emptyIndividual = {
  full_name: "",
  gender: "female",
  date_of_birth: "",
  nida_number: "",
  phone: "",
  email: "",
  physical_address: "",
  employer_name: "",
  monthly_income: "",
  preferred_language: "sw",
  branch_id: "",
};

const emptyBusiness = {
  business_name: "",
  tin: "",
  registration_number: "",
  phone: "",
  email: "",
  physical_address: "",
  business_category: "",
  annual_turnover: "",
  preferred_language: "sw",
  branch_id: "",
};

const defaultBranchId = () => getStoredUser()?.branch_id || "";

const withDefaultBranch = (empty) => ({ ...empty, branch_id: defaultBranchId() });

const BorrowerRegisterModal = ({ isOpen, toggle, onCreated }) => {
  const [tab, setTab] = useState("individual");
  const [individual, setIndividual] = useState(() => withDefaultBranch(emptyIndividual));
  const [business, setBusiness] = useState(() => withDefaultBranch(emptyBusiness));
  const [branches, setBranches] = useState([]);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const branchId = defaultBranchId();
    setIndividual(withDefaultBranch(emptyIndividual));
    setBusiness(withDefaultBranch(emptyBusiness));
    fetchBranches()
      .then((rows) => {
        setBranches(rows);
        if (branchId) {
          setIndividual((prev) => ({ ...prev, branch_id: branchId }));
          setBusiness((prev) => ({ ...prev, branch_id: branchId }));
        }
      })
      .catch(() => setBranches([]));
  }, [isOpen]);

  const reset = () => {
    setTab("individual");
    setIndividual(withDefaultBranch(emptyIndividual));
    setBusiness(withDefaultBranch(emptyBusiness));
    setError("");
  };

  const handleClose = () => {
    reset();
    toggle();
  };

  const submitIndividual = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const payload = {
        ...individual,
        email: individual.email || null,
        physical_address: individual.physical_address || null,
        employer_name: individual.employer_name || null,
        monthly_income: individual.monthly_income ? Number(individual.monthly_income) : null,
        branch_id: individual.branch_id || null,
      };
      const created = await runCrud(() => createIndividualBorrower(payload), {
        successMessage: "Individual borrower registered successfully.",
      });
      onCreated?.(created);
      handleClose();
    } catch {
      /* error toast shown by runCrud */
    } finally {
      setSubmitting(false);
    }
  };

  const submitBusiness = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const payload = {
        ...business,
        email: business.email || null,
        physical_address: business.physical_address || null,
        business_category: business.business_category || null,
        annual_turnover: business.annual_turnover ? Number(business.annual_turnover) : null,
        branch_id: business.branch_id || null,
      };
      const created = await runCrud(() => createBusinessBorrower(payload), {
        successMessage: "Business borrower registered successfully.",
      });
      onCreated?.(created);
      handleClose();
    } catch {
      /* error toast shown by runCrud */
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="lg">
      <ModalHeader toggle={handleClose}>Register borrower</ModalHeader>
      <ModalBody>
        {error && <Alert color="danger">{typeof error === "string" ? error : JSON.stringify(error)}</Alert>}
        <Nav tabs className="border-tab mb-3">
          <NavItem>
            <NavLink className={tab === "individual" ? "active" : ""} onClick={() => setTab("individual")}>
              Individual
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink className={tab === "business" ? "active" : ""} onClick={() => setTab("business")}>
              Business
            </NavLink>
          </NavItem>
        </Nav>
        <TabContent activeTab={tab}>
          <TabPane tabId="individual">
            <Form onSubmit={submitIndividual}>
              <div className="row">
                <div className="col-md-6">
                  <FormGroup>
                    <Label>Full name *</Label>
                    <Input required value={individual.full_name} onChange={(e) => setIndividual({ ...individual, full_name: e.target.value })} />
                  </FormGroup>
                </div>
                <div className="col-md-3">
                  <FormGroup>
                    <Label>Gender *</Label>
                    <Input type="select" value={individual.gender} onChange={(e) => setIndividual({ ...individual, gender: e.target.value })}>
                      <option value="female">Female</option>
                      <option value="male">Male</option>
                    </Input>
                  </FormGroup>
                </div>
                <div className="col-md-3">
                  <FormGroup>
                    <Label>Date of birth *</Label>
                    <Input type="date" required value={individual.date_of_birth} onChange={(e) => setIndividual({ ...individual, date_of_birth: e.target.value })} />
                  </FormGroup>
                </div>
                <div className="col-md-6">
                  <FormGroup>
                    <Label>NIDA number *</Label>
                    <Input required value={individual.nida_number} onChange={(e) => setIndividual({ ...individual, nida_number: e.target.value })} />
                  </FormGroup>
                </div>
                <div className="col-md-6">
                  <FormGroup>
                    <Label>Phone *</Label>
                    <Input required value={individual.phone} onChange={(e) => setIndividual({ ...individual, phone: e.target.value })} placeholder="+255..." />
                  </FormGroup>
                </div>
                <div className="col-md-6">
                  <FormGroup>
                    <Label>Email</Label>
                    <Input type="email" value={individual.email} onChange={(e) => setIndividual({ ...individual, email: e.target.value })} />
                  </FormGroup>
                </div>
                <div className="col-md-6">
                  <FormGroup>
                    <Label>Monthly income (TZS)</Label>
                    <Input type="number" min="0" value={individual.monthly_income} onChange={(e) => setIndividual({ ...individual, monthly_income: e.target.value })} />
                  </FormGroup>
                </div>
                <div className="col-md-12">
                  <FormGroup>
                    <Label>Branch</Label>
                    <Input
                      type="select"
                      value={individual.branch_id}
                      onChange={(e) => setIndividual({ ...individual, branch_id: e.target.value })}>
                      <option value="">— No branch —</option>
                      {branches.map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.name}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>
                </div>
                <div className="col-md-12">
                  <FormGroup>
                    <Label>Address</Label>
                    <Input value={individual.physical_address} onChange={(e) => setIndividual({ ...individual, physical_address: e.target.value })} />
                  </FormGroup>
                </div>
              </div>
              <ModalFooter className="px-0 pb-0">
                <Button color="secondary" type="button" onClick={handleClose}>
                  Cancel
                </Button>
                <Button color="primary" type="submit" disabled={submitting}>
                  {submitting ? "Saving…" : "Register individual"}
                </Button>
              </ModalFooter>
            </Form>
          </TabPane>
          <TabPane tabId="business">
            <Form onSubmit={submitBusiness}>
              <div className="row">
                <div className="col-md-6">
                  <FormGroup>
                    <Label>Business name *</Label>
                    <Input required value={business.business_name} onChange={(e) => setBusiness({ ...business, business_name: e.target.value })} />
                  </FormGroup>
                </div>
                <div className="col-md-6">
                  <FormGroup>
                    <Label>Phone *</Label>
                    <Input required value={business.phone} onChange={(e) => setBusiness({ ...business, phone: e.target.value })} />
                  </FormGroup>
                </div>
                <div className="col-md-6">
                  <FormGroup>
                    <Label>TIN *</Label>
                    <Input required value={business.tin} onChange={(e) => setBusiness({ ...business, tin: e.target.value })} />
                  </FormGroup>
                </div>
                <div className="col-md-6">
                  <FormGroup>
                    <Label>Registration no. *</Label>
                    <Input required value={business.registration_number} onChange={(e) => setBusiness({ ...business, registration_number: e.target.value })} />
                  </FormGroup>
                </div>
                <div className="col-md-6">
                  <FormGroup>
                    <Label>Category</Label>
                    <Input value={business.business_category} onChange={(e) => setBusiness({ ...business, business_category: e.target.value })} />
                  </FormGroup>
                </div>
                <div className="col-md-6">
                  <FormGroup>
                    <Label>Annual turnover (TZS)</Label>
                    <Input type="number" min="0" value={business.annual_turnover} onChange={(e) => setBusiness({ ...business, annual_turnover: e.target.value })} />
                  </FormGroup>
                </div>
                <div className="col-md-12">
                  <FormGroup>
                    <Label>Branch</Label>
                    <Input
                      type="select"
                      value={business.branch_id}
                      onChange={(e) => setBusiness({ ...business, branch_id: e.target.value })}>
                      <option value="">— No branch —</option>
                      {branches.map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.name}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>
                </div>
              </div>
              <ModalFooter className="px-0 pb-0">
                <Button color="secondary" type="button" onClick={handleClose}>
                  Cancel
                </Button>
                <Button color="primary" type="submit" disabled={submitting}>
                  {submitting ? "Saving…" : "Register business"}
                </Button>
              </ModalFooter>
            </Form>
          </TabPane>
        </TabContent>
      </ModalBody>
    </Modal>
  );
};

export default BorrowerRegisterModal;
